import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import API from '../../utils/apiconfig';
import TruncateText from '../../components/TruncateText';
import DataTable from '../../components/ui/DataTable';
import ListToolbar, { ToolbarField } from '../../components/ui/ListToolbar';
import { ds } from '../../design-system';
import AppButton from '../../components/ui/AppButton';
import { formatAppDateTime } from '../../utils/dateFormat';

const REFERRAL_COLUMNS = [
  { key: 'member', label: 'Member' },
  { key: 'code', label: 'Code entered' },
  { key: 'code_status', label: 'Code status' },
  { key: 'referred_by', label: 'Referred by' },
  { key: 'owner', label: 'Owner code' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'source', label: 'Source' },
  { key: 'payment', label: 'Payment' },
  { key: 'claim', label: 'Claim' },
  { key: 'requested', label: 'Requested' },
  { key: 'actions', label: 'Actions' },
];

const adminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_access_token')}`,
});

function formatWhen(iso) {
  return formatAppDateTime(iso);
}

export default function ReferralHistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total_pages: 1, count: 0 });
  const [search, setSearch] = useState('');
  const [claimStatus, setClaimStatus] = useState('all');
  const [mappingStatus, setMappingStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [actionSaving, setActionSaving] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const load = useCallback(async (nextPage = page, overrides = null) => {
    setLoading(true);
    setError('');
    const f = overrides || { search, claimStatus, mappingStatus, dateFrom, dateTo };
    try {
      const params = {
        page: nextPage,
        page_size: pageSize,
      };
      if (String(f.search || '').trim()) params.search = String(f.search).trim();
      if (f.claimStatus && f.claimStatus !== 'all') params.claim_status = f.claimStatus;
      if (f.mappingStatus && f.mappingStatus !== 'all') {
        params.referral_mapping_status = f.mappingStatus;
      }
      if (f.dateFrom) params.date_from = f.dateFrom;
      if (f.dateTo) params.date_to = f.dateTo;

      const res = await api.get(API.ADMIN_REFERRAL_HISTORY, {
        headers: adminHeaders(),
        params,
      });
      const list = res.data?.results || res.data?.items || [];
      setRows(list);
      setMeta({
        page: res.data?.page || nextPage,
        total_pages: res.data?.total_pages || 1,
        count: res.data?.count || list.length,
      });
      setPage(nextPage);
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to load referral history.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, claimStatus, mappingStatus, dateFrom, dateTo]);

  const clearFilters = () => {
    const cleared = {
      search: '',
      claimStatus: 'all',
      mappingStatus: 'all',
      dateFrom: '',
      dateTo: '',
    };
    setSearch(cleared.search);
    setClaimStatus(cleared.claimStatus);
    setMappingStatus(cleared.mappingStatus);
    setDateFrom(cleared.dateFrom);
    setDateTo(cleared.dateTo);
    load(1, cleared);
  };

  // Initial + page-size only. All filter fields apply via Apply Filters (no fetch on change).
  useEffect(() => {
    load(1);
  }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional

  const openDetail = async (row) => {
    setDetailLoading(true);
    setRejectNote('');
    try {
      const res = await api.get(API.ADMIN_REFERRAL_HISTORY_DETAIL(row.user_id), {
        headers: adminHeaders(),
      });
      setDetail(res.data);
    } catch (e) {
      showToast(e.response?.data?.error || 'Unable to load referral detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  const approveClaim = async (claimId) => {
    setActionSaving(true);
    try {
      await api.post(
        API.ADMIN_REFERRAL_CLAIM_APPROVE(claimId),
        {},
        { headers: adminHeaders() },
      );
      showToast('Referral claim approved.');
      if (detail?.user_id) await openDetail({ user_id: detail.user_id });
      load(page);
    } catch (e) {
      showToast(e.response?.data?.error || 'Approve failed.');
    } finally {
      setActionSaving(false);
    }
  };

  const rejectClaim = async (claimId) => {
    if (!rejectNote.trim()) {
      showToast('Rejection reason is required.');
      return;
    }
    setActionSaving(true);
    try {
      await api.post(
        API.ADMIN_REFERRAL_CLAIM_REJECT(claimId),
        { admin_note: rejectNote.trim() },
        { headers: adminHeaders() },
      );
      showToast('Referral claim rejected.');
      setRejectNote('');
      if (detail?.user_id) await openDetail({ user_id: detail.user_id });
      load(page);
    } catch (e) {
      showToast(e.response?.data?.error || 'Reject failed.');
    } finally {
      setActionSaving(false);
    }
  };

  const pendingClaim = detail?.claims?.find((c) => c.status === 'PENDING');

  return (
    <div className="space-y-4">
      {toast ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">
          {toast}
        </div>
      ) : null}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="User, ID, referral code, referred by"
        onApply={() => load(1)}
        onClear={clearFilters}
      >
        <ToolbarField label="Claim status">
          <select value={claimStatus} onChange={(e) => setClaimStatus(e.target.value)} className={ds.select}>
            <option value="all">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="NONE">None</option>
          </select>
        </ToolbarField>
        <ToolbarField label="Mapping">
          <select value={mappingStatus} onChange={(e) => setMappingStatus(e.target.value)} className={ds.select}>
            <option value="all">All</option>
            <option value="mapped">Mapped</option>
            <option value="unmapped">Unmapped</option>
          </select>
        </ToolbarField>
        <ToolbarField label="From">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={ds.input} />
        </ToolbarField>
        <ToolbarField label="To">
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={ds.input} />
        </ToolbarField>
      </ListToolbar>

      {error ? (
        <div className="rounded-ds-xl border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-800">{error}</div>
      ) : null}

      <DataTable
        columns={REFERRAL_COLUMNS}
        loading={loading}
        empty={!loading && rows.length === 0}
        emptyTitle="No referral records found."
        minWidth="72rem"
        mobileScrollHint="Swipe sideways — Member stays visible"
        ariaLabel="Referral history table"
        page={meta.page || page}
        totalPages={meta.total_pages}
        pageSize={pageSize}
        count={meta.count || rows.length}
        onPageChange={(p) => load(p)}
        onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
        paginationDisabled={loading}
        rowsPerPageId="admin-referral-history-rows"
      >
        {rows.map((row) => (
          <tr key={row.user_id} className={ds.row}>
            <td className={`${ds.td} font-semibold text-ink max-w-[160px]`}>
              <TruncateText text={row.username} className="font-semibold text-ink max-w-[160px]" />
            </td>
            <td className={ds.td}>
              <TruncateText text={row.referral_code_entered || '—'} className="max-w-[140px]" as="span" />
            </td>
            <td className={ds.td}>{row.referral_code_status}</td>
            <td className={`${ds.td} max-w-[180px]`}>
              <TruncateText
                text={
                  row.referred_by_username
                    ? `${row.referred_by_username} (#${row.referred_by_user_id})`
                    : '—'
                }
                className="max-w-[180px]"
              />
            </td>
            <td className={ds.td}>{row.referral_owner_code || '—'}</td>
            <td className={ds.td}>{row.referral_mapping_status}</td>
            <td className={ds.td}>{row.mapping_source}</td>
            <td className={`${ds.td} capitalize`}>{row.payment_status || '—'}</td>
            <td className={ds.td}>{row.referral_claim_status}</td>
            <td className={`${ds.td} whitespace-nowrap`}>{formatWhen(row.requested_at)}</td>
            <td className={ds.td}>
              <button type="button" onClick={() => openDetail(row)} className={ds.btnGhost}>
                Timeline
              </button>
            </td>
          </tr>
        ))}
      </DataTable>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-3">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-violet-600">Referral timeline</p>
                <h3 className="text-lg font-black text-slate-900">
                  {detail?.username || 'Loading…'} {detail?.user_id ? `#${detail.user_id}` : ''}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            {detailLoading || !detail ? (
              <p className="p-8 text-center text-slate-500">Loading detail…</p>
            ) : (
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-slate-500">Mapping source</p>
                    <p className="font-bold text-slate-900 mt-0.5">{detail.mapping_source}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-slate-500">Referred by</p>
                    <p className="font-bold text-slate-900 mt-0.5">
                      {detail.referred_by_username || '—'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-slate-500">Claim status</p>
                    <p className="font-bold text-slate-900 mt-0.5">{detail.referral_claim_status}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-slate-500">Payment status</p>
                    <p className="font-bold text-slate-900 mt-0.5 capitalize">{detail.payment_status || '—'}</p>
                  </div>
                </div>

                {pendingClaim ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                    <p className="text-sm font-bold text-amber-950">
                      Pending claim: {pendingClaim.referral_code_entered}
                      {pendingClaim.referrer_username ? ` → ${pendingClaim.referrer_username}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <AppButton
                        type="button"
                        variant="success"
                        icon="check"
                        disabled={actionSaving}
                        onClick={() => approveClaim(pendingClaim.id)}
                      >
                        Approve claim
                      </AppButton>
                    </div>
                    <textarea
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder="Rejection reason (required to reject)"
                      className="w-full rounded-xl border border-amber-200 px-3 py-2 text-sm"
                      rows={2}
                    />
                    <AppButton
                      type="button"
                      variant="danger"
                      icon="x"
                      disabled={actionSaving}
                      onClick={() => rejectClaim(pendingClaim.id)}
                    >
                      Reject claim
                    </AppButton>
                  </div>
                ) : null}

                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3">
                    What happened (step by step)
                  </p>
                  <div className="space-y-0 border-l-2 border-violet-200 ml-2">
                    {(detail.timeline || []).length === 0 ? (
                      <p className="pl-4 text-sm text-slate-500">No audit events recorded yet.</p>
                    ) : (
                      (detail.timeline || []).map((evt) => (
                        <div key={evt.id} className="relative pl-4 pb-4">
                          <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-violet-500 border-2 border-white" />
                          <p className="text-sm font-semibold text-slate-900">{evt.action_label || evt.action}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {formatWhen(evt.created_at)}
                            {evt.actor ? ` · By ${evt.actor}` : ''}
                          </p>
                          {evt.remarks ? (
                            <p className="text-xs text-slate-600 mt-1">{evt.remarks}</p>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
